import { FlatList, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";

import { tCartProduct } from "tmt_api/src/types/types.validator";

import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { tModalComponentProps } from "src/redux/modals/types";
import {
  PdDivider,
  PdTableCell,
  PdTableCellText,
  PdTableEmpty,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/layout/table";
import { getProductInfoFromCartProduct } from "src/util/cart";
import { TesterBadge } from "src/components/ui";
import { PdStack, PdText } from "src/components/layout";
import { PdCheckbox } from "src/components/forms/checkbox";
import React from "react";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { getApiError, putOrderSplit } from "src/util/apiNew";
import { AlertModal, tAlertModalProps } from "src/components/Modals/AlertModal";
import { useAppDispatch } from "src/redux";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useIsMobile } from "src/hooks/ui";
import { PdFieldset } from "src/components/form-control";
import { PdDateInput } from "src/components/forms/input";
import { PdIconButton } from "src/components/forms/icon-button";
import { PdArrowLeft } from "src/components/Icons";
import { getItemPrice } from "src/util/functions";
import { formatMoney } from "src/util/gFormatting";
import { kFonts } from "src/kFonts";

type tModalParams = {
  orderId: string;
  products: tCartProduct[];
};

type tTableProduct = {
  id: string;
  name: string;
  sku: string;
  originalQuantity: number;
  quantity: number;
  newQuantity: number;
  unitPriceAmount: number;
  subtotalAmount: number;
  caseMinimum: number;
  isTester: boolean;
};

const roundInSteps = (value: number, step: number): number => {
  return Math.ceil(value / step) * step;
};

const mapProducts = (data: tCartProduct[]): tTableProduct[] => {
  return data.map((product) => {
    const { caseMinimum, isTester } = getProductInfoFromCartProduct(product);
    return {
      id: product.isTester ? product.tester?.sku ?? `${product.id ?? ""}` : product.actualSKU,
      name: product.name,
      sku: product.actualSKU,
      originalQuantity: product.quantity,
      quantity: product.quantity,
      newQuantity: 0,
      unitPriceAmount: !product.quantity ? 0 : getItemPrice(product) / product.quantity,
      subtotalAmount: 0,
      caseMinimum,
      isTester,
    };
  });
};

const QuantityControls: React.FC<{
  row: tTableProduct;
  onRemove: (value: number, row: tTableProduct) => void;
  onAdd: (value: number, row: tTableProduct) => void;
}> = ({ row, onRemove, onAdd }) => {
  const handleOnRemove = () => {
    const value = roundInSteps(row.newQuantity - row.caseMinimum, row.caseMinimum);
    onRemove(value, row);
  };

  const handleOnAdd = () => {
    const value = roundInSteps(row.newQuantity + row.caseMinimum, row.caseMinimum);
    onAdd(value, row);
  };

  return (
    <View style={styles.controls}>
      <TouchableOpacity style={[styles.alignCenter, styles.justifyCenter, styles.w20]} onPress={handleOnRemove}>
        <PdText style={styles.cartQuantityChangeButtonLabel}>-</PdText>
      </TouchableOpacity>
      <PdText style={styles.quantityInputNumber}>{row.newQuantity}</PdText>
      <TouchableOpacity style={[styles.alignCenter, styles.justifyCenter, styles.w20]} onPress={handleOnAdd}>
        <PdText style={styles.cartQuantityChangeButtonLabel}>+</PdText>
      </TouchableOpacity>
    </View>
  );
};

const ProductsList: React.FC<{
  data: tTableProduct[];
  onRemoveQuantity: (value: number, row: tTableProduct) => void;
  onAddQuantity: (value: number, row: tTableProduct) => void;
  onToggleMoveAll?: (value: boolean, row: tTableProduct) => void;
  onCancel: () => void;
  onCreate: () => void;
}> = ({ data, onRemoveQuantity, onAddQuantity, onToggleMoveAll, onCancel, onCreate }) => {
  const isMobile = useIsMobile();
  const columns: tColumn<tTableProduct>[] = React.useMemo(
    () => [
      {
        id: "name",
        header: "Product",
        accessor: (originalRow) => originalRow.name,
        Cell: ({ value, row, style }) => (
          <View style={[styles.flexRow, styles.flexWrap, styles.alignCenter, style]}>
            <View style={styles.wFull}>
              <PdTableCellText numberOfLines={undefined}>{value}</PdTableCellText>
              <PdTableCellText numberOfLines={undefined} style={[styles.textXs, styles.textGray]}>
                {row.sku}
              </PdTableCellText>
            </View>
            {row.isTester && <TesterBadge style={styles.ml2} />}
          </View>
        ),
        style: styles.tableColumnProduct,
        isSortable: false,
      },
      {
        id: "quantity",
        header: "Original",
        accessor: (originalRow) => originalRow.quantity,
        Cell: ({ value }) => <PdTableCellText style={styles.textGray}>{value}</PdTableCellText>,
        style: styles.tableColumnCommon,
        isSortable: false,
      },
      {
        id: "newQuantity",
        header: "New Order",
        accessor: (originalRow) => originalRow.newQuantity,
        Cell: ({ row }) => <QuantityControls row={row} onRemove={onRemoveQuantity} onAdd={onAddQuantity} />,
        style: styles.tableColumnCommon,
        isSortable: false,
      },
      {
        id: "subtotalAmount",
        header: "Total",
        accessor: (originalRow) => originalRow.subtotalAmount,
        Cell: ({ row }) => <PdTableCellText style={styles.textGray}>{formatMoney(row.subtotalAmount)}</PdTableCellText>,
        style: styles.tableColumnCommon,
        isSortable: false,
      },
      {
        id: "options",
        header: "Move all",
        accessor: (originalRow) => originalRow.quantity,
        Cell: ({ row }) => (
          <PdCheckbox
            value={row.originalQuantity === row.newQuantity}
            onChange={(isChecking) => onToggleMoveAll?.(isChecking, row)}
          />
        ),
        style: styles.tableColumnCommon,
        isSortable: false,
      },
    ],
    [onAddQuantity, onRemoveQuantity, onToggleMoveAll],
  );
  const tableInstance = useTable<tTableProduct>({ data, columns });
  const renderItem = ({ item }: { item: tRowInstance<tTableProduct> }) => {
    return (
      <PdTableRow style={styles.py1}>
        {item.cells.map((cell) => (
          <PdTableCell key={cell.uniqueId} style={[styles.hFull, cell.style]}>
            {cell.render("Cell")}
          </PdTableCell>
        ))}
      </PdTableRow>
    );
  };

  const totals = data.reduce(
    (acc, row) => ({
      quantityOriginal: acc.quantityOriginal + row.quantity,
      quantityNewOrder: acc.quantityNewOrder + row.newQuantity,
      subtotalAmountNewOrder: acc.subtotalAmountNewOrder + row.subtotalAmount,
    }),
    { quantityOriginal: 0, quantityNewOrder: 0, subtotalAmountNewOrder: 0 },
  );

  return (
    <>
      <ScrollView
        style={[styles.wFull, styles.mb5]}
        contentContainerStyle={[styles.px6, styles.minWidthFull]}
        horizontal
      >
        <FlatList
          ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
          ListFooterComponent={
            <>
              <PdDivider style={styles.tableDivider} />
              <PdTableRow style={styles.py1}>
                <PdTableCell style={[styles.hFull, styles.tableColumnProduct]} />
                <PdTableCell style={[styles.hFull, styles.tableColumnCommon]}>
                  <PdTableCellText>{totals.quantityOriginal}</PdTableCellText>
                </PdTableCell>
                <PdTableCell style={[styles.hFull, styles.tableColumnCommon]}>
                  <PdTableCellText>{totals.quantityNewOrder}</PdTableCellText>
                </PdTableCell>
                <PdTableCell style={[styles.hFull, styles.tableColumnCommon]}>
                  <PdTableCellText>{formatMoney(totals.subtotalAmountNewOrder)}</PdTableCellText>
                </PdTableCell>
                <PdTableCell style={[styles.hFull, styles.tableColumnCommon]} />
              </PdTableRow>
            </>
          }
          ListEmptyComponent={PdTableEmpty}
          data={tableInstance.rows}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
        />
      </ScrollView>
      <PdStack
        isReversed={isMobile}
        direction={isMobile ? "vertical" : "horizontal"}
        gap={3}
        style={[styles.px6, styles.wFull, styles.alignCenter, styles.justifyBetween]}
      >
        <PdButton colorScheme="purple" variant="outline" onPress={onCancel}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        {totals.quantityOriginal === 0 && (
          <PdText style={styles.errorLabel}>Each order must contain at least one product</PdText>
        )}
        <PdButton onPress={onCreate} isDisabled={totals.quantityNewOrder === 0 || totals.quantityOriginal === 0}>
          <PdButtonLabel>Create New Order</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const ConfirmForm: React.FC<{ onSubmit?: (shipDate: Date | null) => void }> = ({ onSubmit }) => {
  const [shipDate, setShipDate] = React.useState<Date | null>(null);
  const isMobile = useIsMobile();

  const handleOnChange = (value: Date | null) => {
    setShipDate(value);
  };

  const handleOnSubmit = () => {
    onSubmit?.(shipDate);
  };

  return (
    <>
      <View style={[styles.px6, styles.mb5, styles.zIndex1, styles.wFull]}>
        <PdFieldset style={[styles.textMedium, styles.mb1]}>Step 1</PdFieldset>
        <PdStack direction={isMobile ? "vertical" : "horizontal"} style={isMobile ? {} : styles.alignCenter}>
          <PdText style={styles.mr6}>Select estimated ship date</PdText>
          <PdDateInput selected={shipDate} onChange={handleOnChange} />
        </PdStack>
      </View>
      <View style={[styles.px6, styles.mb20]}>
        <PdFieldset style={[styles.textMedium, styles.mb1]}>Step 2</PdFieldset>
        <PdText style={styles.mb4}>
          Send new order to Retailer. They will receive this order and approve it. Once they approve, you’ll receive
          this as a Pending order in your Orders table.
        </PdText>
        <PdText style={styles.helpText}>
          The original order you&apos;ve just split will now be accepted and go into Awaiting Fulfillment stage.
        </PdText>
      </View>
      <PdButton onPress={handleOnSubmit} isDisabled={!shipDate} style={[styles.px6, styles.mlAuto]}>
        <PdButtonLabel>Submit</PdButtonLabel>
      </PdButton>
    </>
  );
};

const SplitOrderModal: React.FC<tModalComponentProps<tModalParams>> = ({ orderId, products, onClose }) => {
  const dispatch = useAppDispatch();
  const alertModal = useModal<tAlertModalProps>(AlertModal);
  const [data, setData] = React.useState(mapProducts(products));
  const [step, setStep] = React.useState(0);

  const updateProduct = React.useCallback((id: string, product: Partial<tTableProduct>) => {
    setData((prev) => prev.map((prevData) => (prevData.id === id ? { ...prevData, ...product } : prevData)));
  }, []);

  const toggleProductQuantity = React.useCallback(
    (isChecking: boolean, product: tTableProduct) => {
      updateProduct(
        product.id,
        isChecking
          ? {
              quantity: 0,
              newQuantity: product.originalQuantity,
              subtotalAmount: product.originalQuantity * product.unitPriceAmount,
            }
          : { quantity: product.originalQuantity, newQuantity: 0, subtotalAmount: 0 },
      );
    },
    [updateProduct],
  );

  const handleOnRemove = React.useCallback(
    (value: number, product: tTableProduct) => {
      if (value >= 0) {
        updateProduct(product.id, {
          quantity: product.quantity + product.caseMinimum,
          newQuantity: value,
          subtotalAmount: value * product.unitPriceAmount,
        });
      }
    },
    [updateProduct],
  );

  const handleOnAdd = React.useCallback(
    (value: number, product: tTableProduct) => {
      if (value <= product.originalQuantity) {
        updateProduct(product.id, {
          quantity: product.quantity - product.caseMinimum,
          newQuantity: value,
          subtotalAmount: value * product.unitPriceAmount,
        });
      }
    },
    [updateProduct],
  );

  const handleOnClose = () => {
    onClose();
  };

  const handleOnPressCreateNewOrder = () => {
    const _newOrderProducts: { sku: string; quantity: number }[] = [];
    for (const dataRow of data) {
      if (dataRow.newQuantity) {
        _newOrderProducts.push({ sku: dataRow.sku, quantity: dataRow.newQuantity });
      }
    }
    setStep(1);
  };

  const handleOnSubmit = async (shipDate: Date | null) => {
    try {
      const _products: { sku: string; quantity: number }[] = [];
      for (const dataRow of data) {
        if (dataRow.newQuantity) {
          _products.push({ sku: dataRow.sku, quantity: dataRow.newQuantity });
        }
      }
      const response = await putOrderSplit(orderId, {
        products: _products,
        shipDate: shipDate ? shipDate.toISOString() : undefined,
      });

      if (response?.message) {
        alertModal.openAsync({ title: "ATTENTION", description: response.message });
      } else {
        dispatch({ type: "RIBBON", message: "Order Split!" });
      }

      onClose();
    } catch (e) {
      const error = getApiError(e);
      dispatch({ type: "RIBBON", message: new Error(error.message) });
    }
  };

  return (
    <PdModal style={styles.modal}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <View style={[styles.flexRow, styles.alignCenter, styles.px6, styles.mb4]}>
          {step === 1 && (
            <PdIconButton
              onPress={() => setStep(0)}
              variant="ghost"
              icon={<PdArrowLeft style={styles.arrowLeftIcon} />}
              style={styles.mr2}
            />
          )}
          <PdModalTitle>Split Order</PdModalTitle>
        </View>
        {step === 0 && (
          <ProductsList
            data={data}
            onRemoveQuantity={handleOnRemove}
            onAddQuantity={handleOnAdd}
            onToggleMoveAll={toggleProductQuantity}
            onCancel={handleOnClose}
            onCreate={handleOnPressCreateNewOrder}
          />
        )}
        {step === 1 && <ConfirmForm onSubmit={handleOnSubmit} />}
      </ScrollView>
    </PdModal>
  );
};

const useSplitOrderModal = (): tUseModal<tModalParams> => {
  return useModal(SplitOrderModal);
};

export default useSplitOrderModal;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  arrowLeftIcon: {
    width: 22,
  },
  cartQuantityChangeButtonLabel: {
    fontSize: 22,
    fontWeight: "100",
    textAlign: "center",
  },
  controls: {
    alignItems: "center",
    flexDirection: "row",
    textAlign: "center",
  },
  errorLabel: {
    ...kFonts.ralewayExtraSmallSemibold,
    color: kColors.red,
    letterSpacing: 1,
    lineHeight: 18,
    textTransform: "uppercase",
  },
  flexRow: {
    flexDirection: "row",
  },
  flexWrap: {
    flexWrap: "wrap",
  },
  hFull: {
    height: "100%",
  },
  helpText: {
    ...kFonts.poppinsLightSmall,
    color: kColors.gray500,
    letterSpacing: 1.1,
    lineHeight: 18,
    minWidth: 200,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb20: {
    marginBottom: 20 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
  minWidthFull: {
    minWidth: "100%",
  },
  ml2: {
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 800,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  mr6: {
    marginRight: 6 * kStyleConst.kSpacing,
  },
  px6: {
    paddingHorizontal: 6 * kStyleConst.kSpacing,
  },
  py1: {
    paddingVertical: 1 * kStyleConst.kSpacing,
  },
  quantityInputNumber: {
    borderWidth: 0,
    textAlign: "center",
    width: 60,
  },
  tableColumnCommon: {
    flex: 1,
    justifyContent: "center",
  },
  tableColumnProduct: {
    flex: 3,
  },
  tableDivider: {
    marginVertical: kStyleConst.kSpacing * 2,
  },
  textGray: {
    color: kColors.gray500,
  },
  textMedium: {
    fontWeight: "500",
  },
  textXs: {
    fontSize: 12,
  },
  w20: {
    width: 20,
  },
  wFull: {
    width: "100%",
  },
  zIndex1: {
    zIndex: 1,
  },
});
